import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Footer from '../Footer';
import { MEMBER_URLS, PUBLIC_URLS } from '../config/apiConfig';
import '../../styles/CommonStyles.css';

function RegisterPage() {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    // Membership Details - now first
    membershipType: '',
    ibbiRegistrationNumber: '',
    rvoName: '',
    otherRvo: '', // Add field for custom RVO input
    
    // Personal Information - now second
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    
    // Professional Information - now third
    profession: '',
    otherProfession: '', // Added new field for "Others" specification
    organization: '',
    designation: '',
    experience: '',
    
    // Account Information - remains last
    password: '',
    confirmPassword: '',
    agreeTerms: false
  });
  
  const [errors, setErrors] = useState({});
  const [paymentData, setPaymentData] = useState(null);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  // Add new state variables for discounts
  const [availableDiscounts, setAvailableDiscounts] = useState([]);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [isLoadingDiscounts, setIsLoadingDiscounts] = useState(false);
  
  // List of Indian states for dropdown
  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
    'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
    'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
    'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ];
  
  // RVO list for dropdown
  const rvoOptions = [
    'IOV Registered Valuers Foundation',
    'ICAI Registered Valuers Organisation',
    'ICMAI Registered Valuers Organisation',
    'ICSI Registered Valuers Organisation',
    'IIV India Registered Valuers Foundation',
    'CVSRTA Registered Valuers Association',
    'Divya Jyoti Foundation',
    'CEV Integral Appraisers Foundation',
    'NRVA Registered Valuers Association',
    'PVAI Valuation Professional Organisation',
    'Other' // Add "Other" option
  ];
  
  // Profession list for dropdown
  const professionOptions = [
    'CA', 
    'CS', 
    'CMA', 
    'MBA', 
    'CFA', 
    'Advocate',
    'Not Applicable', // Add "Not Applicable" option
    'Others'
  ];
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }));
    }
    
    // Re-validate student membership age when date of birth changes
    if (name === 'dateOfBirth' && value) {
      const isStudentMembership = formData.membershipType === 'student' || 
        formData.membershipType === 'student_member' || 
        membershipPlans.some(plan => 
          plan.name.toLowerCase().includes('student') && 
          formData.membershipType === plan.name.toLowerCase().replace(/\s+/g, '_')
        );
      
      if (isStudentMembership) {
        const today = new Date();
        const birthDate = new Date(value);
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        // Calculate exact age considering months and days
        const exactAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
        
        if (exactAge >= 30) {
          setErrors(prevErrors => ({
            ...prevErrors,
            dateOfBirth: 'You are not eligible for student membership. Student membership is only available for individuals under 30 years of age. Please go back and select a different membership type.'
          }));
        } else {
          // Clear the error if age is valid
          setErrors(prevErrors => ({
            ...prevErrors,
            dateOfBirth: null
          }));
        }
      }
    }
  };
  
  const validateStep = (currentStep) => {
    const newErrors = {};
    
    if (currentStep === 1) {
      // Validate membership type
      if (!formData.membershipType) {
        newErrors.membershipType = 'Please select a membership type';
      }
      
      // Check if student membership is selected and validate age
      const isStudentMembership = formData.membershipType === 'student' || 
        formData.membershipType === 'student_member' || 
        membershipPlans.some(plan => 
          plan.name.toLowerCase().includes('student') && 
          formData.membershipType === plan.name.toLowerCase().replace(/\s+/g, '_')
        );
      
      if (isStudentMembership && formData.dateOfBirth) {
        const today = new Date();
        const birthDate = new Date(formData.dateOfBirth);
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        // Calculate exact age considering months and days
        const exactAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
        
        if (exactAge >= 30) {
          newErrors.membershipType = 'Student membership is only available for individuals under 30 years of age';
        }
      }
      
      // Validate primary membership specific fields if that type is selected
      // Check both by value and name to be safe
      const isPrimaryMembership = formData.membershipType === 'primary' || 
        formData.membershipType === 'primary_member' || 
        membershipPlans.some(plan => 
          plan.name.toLowerCase().includes('primary') && 
          formData.membershipType === plan.name.toLowerCase().replace(/\s+/g, '_')
        );
      
      if (isPrimaryMembership) {
        if (!formData.ibbiRegistrationNumber) {
          newErrors.ibbiRegistrationNumber = 'IBBI Registration Number is required for Primary membership';
        }
        if (!formData.rvoName) {
          newErrors.rvoName = 'RVO Name is required for Primary membership';
        }
        // Validate otherRvo field if "Other" is selected
        if (formData.rvoName === 'Other' && !formData.otherRvo) {
          newErrors.otherRvo = 'Please specify your RVO name';
        }
      }
    } 
    else if (currentStep === 2) {
      // Validate personal information
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
      
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email address is invalid';
      }
      
      if (!formData.phone) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
        newErrors.phone = 'Please enter a valid 10-digit phone number';
      }
      
      if (!formData.address) newErrors.address = 'Address is required';
      if (!formData.city) newErrors.city = 'City is required';
      if (!formData.state) newErrors.state = 'State is required';
      
      if (!formData.pincode) {
        newErrors.pincode = 'Pincode is required';
      } else if (!/^\d{6}$/.test(formData.pincode)) {
        newErrors.pincode = 'Please enter a valid 6-digit pincode';
      }
    }
    else if (currentStep === 3) {
      // Validate professional information
      if (!formData.profession) {
        newErrors.profession = 'Profession is required';
      } else if (formData.profession === 'Others' && !formData.otherProfession) {
        newErrors.otherProfession = 'Please specify your profession';
      }
      
      // Organization is now optional, so we don't validate it
      
      if (!formData.experience) newErrors.experience = 'Experience is required';
    }
    else if (currentStep === 4) {
      // Validate account information
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      
      if (!formData.agreeTerms) {
        newErrors.agreeTerms = 'You must agree to the terms and conditions';
      }
    }
    
    return newErrors;
  };
  
  const handleNextStep = () => {
    const validationErrors = validateStep(step);
    
    // Additional validation when moving from step 2 to step 3
    // Check if student membership was selected and validate age
    if (step === 2) {
      const isStudentMembership = formData.membershipType === 'student' || 
        formData.membershipType === 'student_member' || 
        membershipPlans.some(plan => 
          plan.name.toLowerCase().includes('student') && 
          formData.membershipType === plan.name.toLowerCase().replace(/\s+/g, '_')
        );
      
      if (isStudentMembership && formData.dateOfBirth) {
        const today = new Date();
        const birthDate = new Date(formData.dateOfBirth);
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        // Calculate exact age considering months and days
        const exactAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
        
        if (exactAge >= 30) {
          setErrors(prevErrors => ({
            ...prevErrors,
            dateOfBirth: 'You are not eligible for student membership. Student membership is only available for individuals under 30 years of age.'
          }));
          return; // Prevent moving to next step
        }
      }
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setStep(prevStep => prevStep + 1);
  };
  
  const handlePrevStep = () => {
    setStep(prevStep => prevStep - 1);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateStep(step);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    setSubmissionError(null);
    
    try {
      // Find the selected plan to get accurate price information
      const selectedPlan = membershipPlans.find(
        plan => plan.name.toLowerCase().replace(/\s+/g, '_') === formData.membershipType
      );
      
      if (!selectedPlan) {
        throw new Error("Selected membership plan not found. Please try again.");
      }
      
      // Prepare the data - create a copy to avoid mutating the original state
      const submissionData = { 
        ...formData,
        membershipPlanId: selectedPlan.id,
        membershipPlanName: selectedPlan.name,
        membershipAmount: selectedPlan.price
      };
      
      // If "Other" is selected for RVO, use the custom input
      if (submissionData.rvoName === 'Other') {
        submissionData.rvoName = submissionData.otherRvo;
      }
      
      // Call the backend API to register the member with the member endpoint
      const response = await axios.post(MEMBER_URLS.REGISTER, submissionData);
      
      console.log('Registration initiated:', response.data);
      
      // Store payment data for processing
      setPaymentData(response.data.payment);
      setStep(5); // Move to payment step
    } catch (error) {
      console.error('Registration error:', error);
      
      // Enhanced error handling for email/phone already in use
      if (error.response && error.response.data) {
        const errorMsg = error.response.data.error || '';
        
        // Check for various phrases that might indicate an existing account
        if (errorMsg.toLowerCase().includes('email already exists') || 
            errorMsg.toLowerCase().includes('phone number already exists') ||
            errorMsg.toLowerCase().includes('already registered') ||
            errorMsg.toLowerCase().includes('already have an account')) {
          
          // Special handling for existing accounts
          setSubmissionError(
            <div>
              <p>{error.response.data.error}</p>
              <p style={{ marginTop: '10px' }}>
                <Link to="/member-login" style={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>
                  Click here to login to your existing account
                </Link>
              </p>
            </div>
          );
        } else {
          // Generic error message
          setSubmissionError(error.response.data.error);
        }
      } else {
        setSubmissionError('An unexpected error occurred. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Add function to fetch applicable discounts
  const fetchApplicableDiscounts = async () => {
    setIsLoadingDiscounts(true);
    try {
      // Check if discount was already applied by the backend
      const backendDiscountApplied = 
        paymentData && 
        paymentData.discount_applied && 
        typeof paymentData.discount_applied === 'object';
      
      if (backendDiscountApplied) {
        // If backend already applied a discount, just display it
        setAppliedDiscount({
          code: paymentData.discount_applied.name,
          discountPercent: paymentData.discount_applied.value,
          discountAmount: (paymentData.original_amount - paymentData.amount) / 100,
          description: paymentData.discount_applied.description
        });
        
        // No need to fetch additional discounts since backend already applied one
        console.log('Using backend-applied discount:', paymentData.discount_applied);
        setIsLoadingDiscounts(false);
        return;
      }
      
      // Otherwise fetch discounts from the API
      const response = await axios.get(PUBLIC_URLS.GET_PUBLIC_DISCOUNTS);
      
      if (response.data && response.data.success && response.data.discounts) {
        setAvailableDiscounts(response.data.discounts);
        
        // Find applicable discounts for membership
        const membershipDiscounts = response.data.discounts.filter(
          discount => discount.applicableTo === 'membership' && discount.status === 'active'
        );
        
        // Only auto-apply if backend hasn't already applied a discount
        if (membershipDiscounts.length > 0) {
          const bestDiscount = membershipDiscounts.sort((a, b) => b.value - a.value)[0];
          applyBestDiscount(bestDiscount);
        }
      }
    } catch (error) {
      console.error('Error fetching discounts:', error);
    } finally {
      setIsLoadingDiscounts(false);
    }
  };
  
  // Function to apply the best available discount
  const applyBestDiscount = async (discount) => {
    if (!discount || !paymentData) return;
    
    try {
      // Check if backend already applied a discount
      if (paymentData.discount_applied) {
        console.log('Discount already applied by backend, skipping frontend application');
        return;
      }
      
      // Calculate the discounted amount directly
      const originalAmount = paymentData.amount;
      const discountPercent = discount.value;
      const discountAmount = Math.round(originalAmount * (discountPercent / 100));
      const discountedAmount = originalAmount - discountAmount;
      
      // Update payment data with discounted amount
      setPaymentData(prevData => ({
        ...prevData,
        originalAmount: originalAmount,
        amount: discountedAmount,
        discountAmount: discountAmount,
        discountPercent: discountPercent,
        discountCode: discount.name,
        discountId: discount.id
      }));
      
      setAppliedDiscount({
        code: discount.name,
        discountPercent: discountPercent,
        discountAmount: discountAmount / 100,
        description: discount.description
      });
    } catch (error) {
      console.error('Error applying discount:', error);
    }
  };
  
  // Fetch discounts when payment data is available
  // We use a ref to track if we've already fetched discounts for this payment
  const discountsFetchedRef = React.useRef(false);

  useEffect(() => {
    // Only fetch if we have payment data, we're on step 5, and we haven't fetched yet for this payment
    if (paymentData && step === 5 && !discountsFetchedRef.current) {
      discountsFetchedRef.current = true; // Mark as fetched
      fetchApplicableDiscounts();
    }
    
    // Reset the ref when payment data changes or we leave step 5
    return () => {
      if (step !== 5) {
        discountsFetchedRef.current = false;
      }
    };
  }, [paymentData, step]);
  
  const handlePayment = () => {
    if (!paymentData) {
      setSubmissionError('Payment information is missing. Please try again.');
      return;
    }
    
    // Reset payment status and errors
    setPaymentStatus(null);
    setSubmissionError(null);
    setIsProcessingPayment(true);
    
    const options = {
      key: paymentData.razorpay_key_id,
      amount: paymentData.amount,
      currency: paymentData.currency,
      name: 'Bangalore Valuers Association',
      description: 'Membership Registration',
      order_id: paymentData.razorpay_order_id,
      handler: async function (response) {
        try {
          // Show loading state while verifying payment
          setIsProcessingPayment(true);
          setPaymentStatus('verifying');
          
          // Verify payment with server - use member endpoint
          const verifyResponse = await axios.post(MEMBER_URLS.VERIFY_REGISTRATION, {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            member_id: paymentData.member_id,
            discountId: paymentData.discountId || null, // Include discount ID if applied
            discountCode: paymentData.discountCode || null
          });
          
          if (verifyResponse.data.success) {
            // Store token if provided
            if (verifyResponse.data.token) {
              localStorage.setItem('memberToken', verifyResponse.data.token);
              localStorage.setItem('memberId', verifyResponse.data.member.id);
              localStorage.setItem('memberName', verifyResponse.data.member.name);
            }
            
            // Set payment status to success
            setPaymentStatus('success');
            
            // Move to success step
            setStep(6);
          } else {
            setPaymentStatus('failed');
            setSubmissionError(verifyResponse.data.message || 'Payment verification failed. Please contact support.');
          }
        } catch (error) {
          console.error('Payment verification error:', error);
          
          setPaymentStatus('failed');
          if (error.response && error.response.data && error.response.data.error) {
            setSubmissionError(error.response.data.error);
          } else {
            setSubmissionError('Payment verification failed. Please contact support.');
          }
        } finally {
          setIsProcessingPayment(false);
        }
      },
      prefill: {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        contact: formData.phone
      },
      theme: {
        color: '#002147'
      },
      modal: {
        ondismiss: function() {
          // Handle modal dismissal
          setPaymentStatus('cancelled');
          setSubmissionError('Payment was cancelled or the payment window was closed. Please try again.');
          setIsProcessingPayment(false);
        },
        escape: true,
        animation: true
      }
    };
    
    try {
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function(response) {
        // Handle payment failure
        console.error('Payment failed:', response.error);
        setPaymentStatus('failed');
        setSubmissionError(`Payment failed: ${response.error.description || response.error.reason || 'Error processing payment'}`);
        setIsProcessingPayment(false);
      });
      
      razorpay.open();
    } catch (error) {
      console.error('Error opening Razorpay:', error);
      setPaymentStatus('failed');
      setSubmissionError('Could not initialize payment gateway. Please try again later.');
      setIsProcessingPayment(false);
    }
  };
  
  // Fetch membership plans from the backend
  useEffect(() => {
    fetchMembershipPlans();
  }, []);

  const fetchMembershipPlans = async () => {
    setIsLoadingPlans(true);
    try {
      const response = await axios.get(PUBLIC_URLS.GET_MEMBERSHIP_PLANS);
      if (response.data && response.data.success) {
        setMembershipPlans(response.data.plans);
      }
    } catch (error) {
      console.error('Error fetching membership plans:', error);
    } finally {
      setIsLoadingPlans(false);
    }
  };
  
  // Helper function to get the selected plan
  const getSelectedPlan = () => {
    return membershipPlans.find(
      plan => plan.name.toLowerCase().replace(/\s+/g, '_') === formData.membershipType
    );
  };
  
  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '0';
    return amount.toLocaleString('en-IN');
  };
  
  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="step-content">
            <h3 style={{ marginBottom: '1.5rem', textAlign: 'center' }}>Select Membership Type</h3>
            
            <div style={{ marginBottom: '2rem' }}>
              <p style={{ marginBottom: '1.5rem', textAlign: 'center', color: '#555' }}>
                Please select the type of membership you're applying for:
              </p>
              
              {isLoadingPlans ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div style={{ 
                    width: '40px',
                    height: '40px',
                    border: '3px solid rgba(0,0,0,0.1)',
                    borderTop: '3px solid var(--primary-color)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto'
                  }}></div>
                  <p style={{ marginTop: '1rem' }}>Loading membership plans...</p>
                </div>
              ) : membershipPlans.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                  {membershipPlans.map((plan, index) => {
                    // Convert plan name to lowercase and remove spaces for value
                    const planValue = plan.name.toLowerCase().replace(/\s+/g, '_');
                    // Check if this is a primary membership plan
                    const isPrimary = plan.name.toLowerCase().includes('primary');
                    
                    return (
                      <div 
                        key={index}
                        style={{ 
                          padding: '1.2rem', 
                          border: formData.membershipType === planValue ? '2px solid var(--primary-color)' : '1px solid #ddd',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor: formData.membershipType === planValue ? 'rgba(var(--primary-rgb), 0.05)' : 'white',
                          transition: 'all 0.3s ease',
                          boxShadow: formData.membershipType === planValue ? '0 4px 8px rgba(0,0,0,0.05)' : 'none'
                        }}
                        onClick={() => handleChange({ target: { name: 'membershipType', value: planValue } })}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.7rem' }}>
                          <input 
                            type="radio" 
                            id={planValue} 
                            name="membershipType" 
                            value={planValue}
                            checked={formData.membershipType === planValue}
                            onChange={handleChange}
                            style={{ marginRight: '0.8rem', transform: 'scale(1.2)' }}
                          />
                          <label 
                            htmlFor={planValue} 
                            style={{ 
                              fontWeight: '700', 
                              margin: 0, 
                              fontSize: '1.1rem', 
                              color: formData.membershipType === planValue ? 'var(--primary-color)' : '#333' 
                            }}
                          >
                            {plan.name}
                          </label>
                        </div>
                        <p style={{ marginLeft: '2rem', fontSize: '0.95rem', lineHeight: '1.5' }}>
                          {plan.description}
                          <br />
                          <strong style={{ color: 'var(--primary-color)', display: 'block', marginTop: '0.5rem', fontSize: '1.1rem' }}>
                            Annual Fee: ₹{plan.price}
                          </strong>
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
                  No membership plans are currently available. Please check back later or contact us for assistance.
                </p>
              )}
              
              {errors.membershipType && (
                <p style={{ color: '#dc3545', fontSize: '0.95rem', marginTop: '1rem', textAlign: 'center' }}>
                  {errors.membershipType}
                </p>
              )}
            </div>
            
            {/* Conditional fields for Primary membership */}
            {membershipPlans.some(plan => 
              plan.name.toLowerCase().includes('primary') && 
              formData.membershipType === plan.name.toLowerCase().replace(/\s+/g, '_')
            ) && (
              <div style={{ 
                backgroundColor: '#f9f9f9',
                padding: '1.5rem',
                borderRadius: '8px',
                marginTop: '1rem',
                border: '1px solid #eee'
              }}>
                <h4 style={{ marginBottom: '1.2rem', fontSize: '1.1rem', color: '#333' }}>
                  Additional Information for Primary Members
                </h4>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <label htmlFor="ibbiRegistrationNumber" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    IBBI Registration Number *
                  </label>
                  <input 
                    type="text" 
                    id="ibbiRegistrationNumber" 
                    name="ibbiRegistrationNumber" 
                    value={formData.ibbiRegistrationNumber}
                    onChange={handleChange}
                    placeholder="Enter your IBBI Registration Number"
                    style={{ 
                      width: '100%', 
                      padding: '0.8rem', 
                      borderRadius: '4px', 
                      border: errors.ibbiRegistrationNumber ? '1px solid #dc3545' : '1px solid #ddd',
                      fontSize: '1rem'
                    }}
                  />
                  {errors.ibbiRegistrationNumber && (
                    <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                      {errors.ibbiRegistrationNumber}
                    </p>
                  )}
                </div>
                
                <div style={{ marginBottom: formData.rvoName === 'Other' ? '0.5rem' : '0.5rem' }}>
                  <label htmlFor="rvoName" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Registered Valuers Organization (RVO) *
                  </label>
                  <select 
                    id="rvoName" 
                    name="rvoName" 
                    value={formData.rvoName}
                    onChange={handleChange}
                    style={{ 
                      width: '100%', 
                      padding: '0.8rem', 
                      borderRadius: '4px', 
                      border: errors.rvoName ? '1px solid #dc3545' : '1px solid #ddd',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select RVO</option>
                    {rvoOptions.map((rvo, index) => (
                      <option key={index} value={rvo}>{rvo}</option>
                    ))}
                  </select>
                  {errors.rvoName && (
                    <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                      {errors.rvoName}
                    </p>
                  )}
                </div>
                
                {/* Custom RVO input field when "Other" is selected */}
                {formData.rvoName === 'Other' && (
                  <div style={{ marginBottom: '0.5rem' }}>
                    <label htmlFor="otherRvo" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Specify RVO Name *
                    </label>
                    <input 
                      type="text" 
                      id="otherRvo" 
                      name="otherRvo" 
                      value={formData.otherRvo}
                      onChange={handleChange}
                      placeholder="Enter your RVO name"
                      style={{ 
                        width: '100%', 
                        padding: '0.8rem', 
                        borderRadius: '4px', 
                        border: errors.otherRvo ? '1px solid #dc3545' : '1px solid #ddd',
                        fontSize: '1rem'
                      }}
                    />
                    {errors.otherRvo && (
                      <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                        {errors.otherRvo}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
              <button 
                type="button" 
                onClick={handleNextStep}
                className="btn btn-primary"
                disabled={!formData.membershipType}
                style={{ 
                  backgroundColor: 'var(--primary-color)', 
                  color: 'white',
                  padding: '0.9rem 2rem',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  borderRadius: '6px',
                  border: 'none',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  opacity: !formData.membershipType ? 0.6 : 1,
                  cursor: !formData.membershipType ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Continue
              </button>
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="step-content">
            <h3 style={{ marginBottom: '1.5rem' }}>Personal Information</h3>
            
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
              <div style={{ flex: '1', minWidth: '200px' }}>
                <label htmlFor="firstName" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  First Name *
                </label>
                <input 
                  type="text" 
                  id="firstName" 
                  name="firstName" 
                  value={formData.firstName}
                  onChange={handleChange}
                  style={{ 
                    width: '100%', 
                    padding: '0.8rem', 
                    borderRadius: '4px', 
                    border: errors.firstName ? '1px solid #dc3545' : '1px solid #ddd',
                    fontSize: '1rem'
                  }}
                />
                {errors.firstName && (
                  <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    {errors.firstName}
                  </p>
                )}
              </div>
              
              <div style={{ flex: '1', minWidth: '200px' }}>
                <label htmlFor="lastName" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Last Name *
                </label>
                <input 
                  type="text" 
                  id="lastName" 
                  name="lastName" 
                  value={formData.lastName}
                  onChange={handleChange}
                  style={{ 
                    width: '100%', 
                    padding: '0.8rem', 
                    borderRadius: '4px', 
                    border: errors.lastName ? '1px solid #dc3545' : '1px solid #ddd',
                    fontSize: '1rem'
                  }}
                />
                {errors.lastName && (
                  <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="dateOfBirth" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Date of Birth *
              </label>
              <input 
                type="date" 
                id="dateOfBirth" 
                name="dateOfBirth" 
                value={formData.dateOfBirth}
                onChange={handleChange}
                style={{ 
                  width: '100%', 
                  padding: '0.8rem', 
                  borderRadius: '4px', 
                  border: errors.dateOfBirth ? '1px solid #dc3545' : '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
              {errors.dateOfBirth && (
                <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {errors.dateOfBirth}
                </p>
              )}
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Email Address *
              </label>
              <input 
                type="email" 
                id="email" 
                name="email" 
                value={formData.email}
                onChange={handleChange}
                style={{ 
                  width: '100%', 
                  padding: '0.8rem', 
                  borderRadius: '4px', 
                  border: errors.email ? '1px solid #dc3545' : '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
              {errors.email && (
                <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {errors.email}
                </p>
              )}
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="phone" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Phone Number * (Must be unique)
              </label>
              <input 
                type="tel" 
                id="phone" 
                name="phone" 
                value={formData.phone}
                onChange={handleChange}
                style={{ 
                  width: '100%', 
                  padding: '0.8rem', 
                  borderRadius: '4px', 
                  border: errors.phone ? '1px solid #dc3545' : '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
              {errors.phone && (
                <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {errors.phone}
                </p>
              )}
              <p style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: '0.5rem' }}>
                Please enter a unique phone number. This will be used for account verification.
              </p>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="address" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Address *
              </label>
              <textarea 
                id="address" 
                name="address" 
                value={formData.address}
                onChange={handleChange}
                rows="3"
                style={{ 
                  width: '100%', 
                  padding: '0.8rem', 
                  borderRadius: '4px', 
                  border: errors.address ? '1px solid #dc3545' : '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
              {errors.address && (
                <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {errors.address}
                </p>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
              <div style={{ flex: '1', minWidth: '200px' }}>
                <label htmlFor="city" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  City *
                </label>
                <input 
                  type="text" 
                  id="city" 
                  name="city" 
                  value={formData.city}
                  onChange={handleChange}
                  style={{ 
                    width: '100%', 
                    padding: '0.8rem', 
                    borderRadius: '4px', 
                    border: errors.city ? '1px solid #dc3545' : '1px solid #ddd',
                    fontSize: '1rem'
                  }}
                />
                {errors.city && (
                  <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    {errors.city}
                  </p>
                )}
              </div>
              
              <div style={{ flex: '1', minWidth: '200px' }}>
                <label htmlFor="state" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  State *
                </label>
                <select 
                  id="state" 
                  name="state" 
                  value={formData.state}
                  onChange={handleChange}
                  style={{ 
                    width: '100%', 
                    padding: '0.8rem', 
                    borderRadius: '4px', 
                    border: errors.state ? '1px solid #dc3545' : '1px solid #ddd',
                    fontSize: '1rem'
                  }}
                >
                  <option value="">Select state</option>
                  {indianStates.map((state, index) => (
                    <option key={index} value={state}>{state}</option>
                  ))}
                </select>
                {errors.state && (
                  <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    {errors.state}
                  </p>
                )}
              </div>
              
              <div style={{ flex: '1', minWidth: '150px' }}>
                <label htmlFor="pincode" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Pincode *
                </label>
                <input 
                  type="text" 
                  id="pincode" 
                  name="pincode" 
                  value={formData.pincode}
                  onChange={handleChange}
                  maxLength="6"
                  style={{ 
                    width: '100%', 
                    padding: '0.8rem', 
                    borderRadius: '4px', 
                    border: errors.pincode ? '1px solid #dc3545' : '1px solid #ddd',
                    fontSize: '1rem'
                  }}
                />
                {errors.pincode && (
                  <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    {errors.pincode}
                  </p>
                )}
              </div>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button 
                type="button" 
                onClick={handlePrevStep}
                className="btn btn-light"
                style={{ 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #ddd',
                  padding: '0.8rem 1.5rem',
                  fontSize: '1rem',
                  borderRadius: '4px'
                }}
              >
                Previous
              </button>
              <button 
                type="button" 
                onClick={handleNextStep}
                className="btn btn-primary"
                style={{ 
                  backgroundColor: 'var(--primary-color)', 
                  color: 'white',
                  padding: '0.8rem 1.5rem',
                  fontSize: '1rem',
                  borderRadius: '4px'
                }}
              >
                Next
              </button>
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="step-content">
            <h3 style={{ marginBottom: '1.5rem' }}>Professional Information</h3>
            
            <div style={{ marginBottom: formData.profession === 'Others' ? '0.5rem' : '1.5rem' }}>
              <label htmlFor="profession" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Profession *
              </label>
              <select 
                id="profession" 
                name="profession" 
                value={formData.profession}
                onChange={handleChange}
                style={{ 
                  width: '100%', 
                  padding: '0.8rem', 
                  borderRadius: '4px', 
                  border: errors.profession ? '1px solid #dc3545' : '1px solid #ddd',
                  fontSize: '1rem'
                }}
              >
                <option value="">Select profession</option>
                {professionOptions.map((option, index) => (
                  <option key={index} value={option}>{option}</option>
                ))}
              </select>
              {errors.profession && (
                <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {errors.profession}
                </p>
              )}
            </div>
            
            {/* Display text field when "Others" is selected */}
            {formData.profession === 'Others' && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="otherProfession" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Please Specify *
                </label>
                <input 
                  type="text" 
                  id="otherProfession" 
                  name="otherProfession" 
                  value={formData.otherProfession}
                  onChange={handleChange}
                  placeholder="Specify your profession"
                  style={{ 
                    width: '100%', 
                    padding: '0.8rem', 
                    borderRadius: '4px', 
                    border: errors.otherProfession ? '1px solid #dc3545' : '1px solid #ddd',
                    fontSize: '1rem'
                  }}
                />
                {errors.otherProfession && (
                  <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    {errors.otherProfession}
                  </p>
                )}
              </div>
            )}
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="organization" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Organization <span style={{ fontWeight: 'normal', fontSize: '0.9rem', color: '#6c757d' }}>(Optional)</span>
              </label>
              <input 
                type="text" 
                id="organization" 
                name="organization" 
                value={formData.organization}
                onChange={handleChange}
                style={{ 
                  width: '100%', 
                  padding: '0.8rem', 
                  borderRadius: '4px', 
                  border: errors.organization ? '1px solid #dc3545' : '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
              {errors.organization && (
                <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {errors.organization}
                </p>
              )}
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="designation" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Designation <span style={{ fontWeight: 'normal', fontSize: '0.9rem', color: '#6c757d' }}>(Optional)</span>
              </label>
              <input 
                type="text" 
                id="designation" 
                name="designation" 
                value={formData.designation}
                onChange={handleChange}
                style={{ 
                  width: '100%', 
                  padding: '0.8rem', 
                  borderRadius: '4px', 
                  border: errors.designation ? '1px solid #dc3545' : '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="experience" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Years of Experience *
              </label>
              <select 
                id="experience" 
                name="experience" 
                value={formData.experience}
                onChange={handleChange}
                style={{ 
                  width: '100%', 
                  padding: '0.8rem', 
                  borderRadius: '4px', 
                  border: errors.experience ? '1px solid #dc3545' : '1px solid #ddd',
                  fontSize: '1rem'
                }}
              >
                <option value="">Select years of experience</option>
                <option value="0-2">0-2 years</option>
                <option value="3-5">3-5 years</option>
                <option value="6-10">6-10 years</option>
                <option value="10+">More than 10 years</option>
                <option value="Not Applicable">Not Applicable</option>
              </select>
              {errors.experience && (
                <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {errors.experience}
                </p>
              )}
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button 
                type="button" 
                onClick={handlePrevStep}
                className="btn btn-light"
                style={{ 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #ddd',
                  padding: '0.8rem 1.5rem',
                  fontSize: '1rem'
                }}
              >
                Previous
              </button>
              <button 
                type="button" 
                onClick={handleNextStep}
                className="btn btn-primary"
                style={{ 
                  backgroundColor: 'var(--primary-color)', 
                  color: 'white',
                  padding: '0.8rem 1.5rem',
                  fontSize: '1rem'
                }}
              >
                Next
              </button>
            </div>
          </div>
        );
      
      case 4:
        return (
          <div className="step-content">
            <h3 style={{ marginBottom: '1.5rem' }}>Account Setup</h3>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="password" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Password *
              </label>
              <input 
                type="password" 
                id="password" 
                name="password" 
                value={formData.password}
                onChange={handleChange}
                style={{ 
                  width: '100%', 
                  padding: '0.8rem', 
                  borderRadius: '4px', 
                  border: errors.password ? '1px solid #dc3545' : '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
              {errors.password && (
                <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {errors.password}
                </p>
              )}
              <p style={{ fontSize: '0.9rem', color: '#6c757d', marginTop: '0.5rem' }}>
                Password must be at least 8 characters long.
              </p>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Confirm Password *
              </label>
              <input 
                type="password" 
                id="confirmPassword" 
                name="confirmPassword" 
                value={formData.confirmPassword}
                onChange={handleChange}
                style={{ 
                  width: '100%', 
                  padding: '0.8rem', 
                  borderRadius: '4px', 
                  border: errors.confirmPassword ? '1px solid #dc3545' : '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
              {errors.confirmPassword && (
                <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {errors.confirmPassword}
                </p>
              )}
            </div>
            
            <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'flex-start' }}>
              <input 
                type="checkbox" 
                id="agreeTerms" 
                name="agreeTerms" 
                checked={formData.agreeTerms}
                onChange={handleChange}
                style={{ marginRight: '0.5rem', marginTop: '0.3rem' }}
              />
              <label htmlFor="agreeTerms" style={{ fontSize: '0.95rem' }}>
                I agree to the <Link to="/terms-conditions" style={{ color: 'var(--primary-color)' }}>Terms and Conditions</Link> and <Link to="/privacy-policy" style={{ color: 'var(--primary-color)' }}>Privacy Policy</Link>
              </label>
            </div>
            
            {errors.agreeTerms && (
              <p style={{ color: '#dc3545', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                {errors.agreeTerms}
              </p>
            )}
            
            {/* Enhanced submission error display with login link when applicable */}
            {submissionError && (
              <div style={{ 
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '1rem',
                marginBottom: '1.5rem',
                borderRadius: '0.25rem',
                border: '1px solid #f5c6cb'
              }}>
                {typeof submissionError === 'string' ? (
                  <p style={{ margin: 0 }}>{submissionError}</p>
                ) : (
                  submissionError
                )}
              </div>
            )}
            
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button 
                type="button" 
                onClick={handlePrevStep}
                className="btn btn-light"
                disabled={isSubmitting}
                style={{ 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #ddd',
                  padding: '0.8rem 1.5rem',
                  fontSize: '1rem',
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                Previous
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting}
                style={{ 
                  backgroundColor: 'var(--primary-color)', 
                  color: 'white',
                  padding: '0.8rem 1.5rem',
                  fontSize: '1rem',
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '120px'
                }}
              >
                {isSubmitting ? (
                  <>
                    <span style={{ 
                      width: '20px',
                      height: '20px',
                      border: '3px solid rgba(255,255,255,0.3)',
                      borderTop: '3px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginRight: '10px',
                      display: 'inline-block'
                    }}></span>
                    Registering...
                  </>
                ) : 'Complete Registration'}
              </button>
            </div>
          </div>
        );
      
      case 5:
        // Get selected plan
        const selectedPlan = getSelectedPlan();
        
        // Calculate display amounts - Use backend provided values if available
        const originalAmount = paymentData?.original_amount ? 
          paymentData.original_amount / 100 : 
          (paymentData?.originalAmount ? 
            paymentData.originalAmount / 100 : 
            (selectedPlan ? selectedPlan.price : 0));
            
        const finalAmount = paymentData?.amount ? 
          paymentData.amount / 100 : 
          (selectedPlan ? selectedPlan.price : 0);
        
        // Calculate discount amount based on original and final amounts
        const discountAmount = originalAmount - finalAmount;

        return (
          <div className="step-content" style={{ textAlign: 'center' }}>
            <h3 style={{ marginBottom: '1.5rem', color: 'var(--primary-color)', fontSize: '1.5rem' }}>
              Complete Payment to Activate Membership
            </h3>
            <p style={{ marginBottom: '2rem', fontSize: '1.1rem', lineHeight: '1.6' }}>
              You're almost there! Please complete the payment to activate your BVA membership.
            </p>
            
            {isLoadingDiscounts && (
              <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ 
                  width: '20px',
                  height: '20px',
                  border: '3px solid rgba(0,0,0,0.1)',
                  borderTop: '3px solid var(--primary-color)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '10px'
                }}></div>
                <span>Checking for applicable discounts...</span>
              </div>
            )}
            
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '1.5rem', 
              borderRadius: '8px',
              marginBottom: '2rem',
              maxWidth: '400px',
              margin: '0 auto 2rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <span>Membership Type:</span>
                <strong>
                  {selectedPlan ? selectedPlan.name : formData.membershipType.replace(/_/g, ' ').toUpperCase()}
                </strong>
              </div>
              
              {/* Show original price if a discount was applied */}
              {appliedDiscount && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Original Price:</span>
                  <span style={{ textDecoration: 'line-through' }}>₹{formatCurrency(originalAmount)}</span>
                </div>
              )}
              
              {/* Show discount info if applied */}
              {appliedDiscount && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: 'green' }}>
                  <span>
                    Discount {appliedDiscount.code && <span>({appliedDiscount.code})</span>}:
                  </span>
                  <span>-₹{formatCurrency(discountAmount)}</span>
                </div>
              )}
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '1rem', 
                fontWeight: 'bold',
                borderTop: appliedDiscount ? '1px dashed #ddd' : 'none',
                paddingTop: appliedDiscount ? '0.8rem' : '0'
              }}>
                <span>Final Amount:</span>
                <strong>₹{formatCurrency(finalAmount)}</strong>
              </div>
            </div>
            
            {appliedDiscount && (
              <div style={{ 
                backgroundColor: '#d4edda', 
                color: '#155724', 
                padding: '0.75rem 1.25rem',
                marginBottom: '1.5rem',
                borderRadius: '0.25rem',
                maxWidth: '400px',
                margin: '0 auto 1.5rem',
                border: '1px solid #c3e6cb'
              }}>
                <p style={{ margin: 0 }}>
                  <strong>{appliedDiscount.code}</strong>: {appliedDiscount.discountPercent}% discount applied!
                  {appliedDiscount.description && (
                    <span style={{ display: 'block', fontSize: '0.9rem', marginTop: '0.3rem' }}>
                      {appliedDiscount.description}
                    </span>
                  )}
                </p>
              </div>
            )}
            
            {submissionError && (
              <div style={{ 
                backgroundColor: paymentStatus === 'cancelled' ? '#fff3cd' : '#f8d7da',
                color: paymentStatus === 'cancelled' ? '#856404' : '#721c24',
                padding: '0.75rem 1.25rem',
                marginBottom: '1.5rem',
                borderRadius: '0.25rem',
                maxWidth: '400px',
                margin: '0 auto 1.5rem',
                border: paymentStatus === 'cancelled' ? '1px solid #ffeeba' : '1px solid #f5c6cb'
              }}>
                <p style={{ margin: 0 }}>{submissionError}</p>
              </div>
            )}
            
            <button 
              onClick={handlePayment}
              className="btn btn-primary"
              disabled={isProcessingPayment || isLoadingDiscounts}
              style={{ 
                backgroundColor: 'var(--primary-color)', 
                color: 'white',
                padding: '0.9rem 2rem',
                fontSize: '1.1rem',
                borderRadius: '6px',
                border: 'none',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                fontWeight: '600',
                opacity: (isProcessingPayment || isLoadingDiscounts) ? 0.7 : 1,
                cursor: (isProcessingPayment || isLoadingDiscounts) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto'
              }}
            >
              {isProcessingPayment ? (
                <>
                  <span style={{ 
                    width: '20px',
                    height: '20px',
                    border: '3px solid rgba(255,255,255,0.3)',
                    borderTop: '3px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginRight: '10px',
                    display: 'inline-block'
                  }}></span>
                  Processing...
                </>
              ) : paymentStatus === 'failed' || paymentStatus === 'cancelled' ? 
                  'Retry Payment' : 'Proceed to Payment'}
            </button>
            
            {(paymentStatus === 'failed' || paymentStatus === 'cancelled') && (
              <div style={{ marginTop: '1.5rem' }}>
                <button 
                  onClick={() => navigate('/member-login')}
                  className="btn btn-link"
                  style={{ color: 'var(--primary-color)', textDecoration: 'underline', background: 'none', border: 'none' }}
                >
                  Skip payment for now
                </button>
                <p style={{ fontSize: '0.9rem', color: '#6c757d', marginTop: '0.5rem' }}>
                  Note: Your membership will be limited until payment is completed.
                </p>
              </div>
            )}
            
            <p style={{ marginTop: '1.5rem', fontSize: '0.9rem', color: '#6c757d' }}>
              Note: Your membership will only be activated after successful payment.
            </p>
          </div>
        );
      
      case 6: // Success page after payment
        return (
          <div className="step-content" style={{ textAlign: 'center' }}>
            <div style={{ 
              width: '90px', 
              height: '90px', 
              backgroundColor: '#4BB543', 
              borderRadius: '50%', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 2rem',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }}>
              <span style={{ color: 'white', fontSize: '3rem' }}>✓</span>
            </div>
            <h3 style={{ marginBottom: '1.5rem', color: 'var(--primary-color)', fontSize: '1.8rem' }}>
              Registration Successful!
            </h3>
            <p style={{ marginBottom: '2rem', fontSize: '1.1rem', lineHeight: '1.6' }}>
              Thank you for registering with Bangalore Valuers' Association. Your membership is now active!
            </p>
            <p style={{ marginBottom: '2rem', fontSize: '1.1rem', color: '#555' }}>
              Please check your email for confirmation and additional information.
            </p>
            <Link to="/member-dashboard">
              <button 
                className="btn btn-primary"
                style={{ 
                  backgroundColor: 'var(--primary-color)', 
                  color: 'white',
                  padding: '0.9rem 2rem',
                  fontSize: '1.1rem',
                  borderRadius: '6px',
                  border: 'none',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  fontWeight: '600'
                }}
              >
                Go to Dashboard
              </button>
            </Link>
          </div>
        );
      
      default:
        return null;
    }
  };
  
  return (
    <div className="page-container">
      <Header />
      
      {/* Payment Verification Loading Overlay */}
      {paymentStatus === 'verifying' && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            border: '6px solid rgba(255, 255, 255, 0.3)',
            borderTop: '6px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: 'white',
            fontSize: '1.2rem',
            marginTop: '1.5rem',
            fontWeight: '500'
          }}>
            Verifying your payment...
          </p>
          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '0.9rem',
            marginTop: '0.5rem'
          }}>
            Please wait, this may take a few moments
          </p>
        </div>
      )}
      
      <div className="page-header">
        <h1>REGISTER</h1>
        <p>Join the Bangalore Valuers' Association</p>
      </div>
      
      <section className="content-section">
        {/* Add a style for the spinner animation */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            :root {
              --primary-rgb: 0, 123, 255; /* Add this for rgba usage */
            }
          `}
        </style>
        
        {step < 5 && (
          <div className="steps-progress" style={{ 
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '2.5rem'
          }}>
            {[1, 2, 3, 4].map(stepNum => (
              <div key={stepNum} style={{ 
                display: 'flex', 
                alignItems: 'center' 
              }}>
                <div style={{ 
                  width: '36px', 
                  height: '36px',
                  backgroundColor: step >= stepNum ? 'var(--primary-color)' : '#f8f9fa',
                  color: step >= stepNum ? 'white' : '#6c757d',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600',
                  border: step === stepNum ? '2px solid var(--secondary-color)' : '1px solid #ddd',
                  boxShadow: step >= stepNum ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  position: 'relative',
                  zIndex: 2
                }}>
                  {stepNum}
                </div>
                
                {stepNum < 4 && (
                  <div style={{ 
                    height: '3px', 
                    width: '100px', 
                    backgroundColor: step > stepNum ? 'var(--primary-color)' : '#f0f0f0',
                    margin: '0 0.25rem',
                    position: 'relative',
                    zIndex: 1
                  }}></div>
                )}
              </div>
            ))}
          </div>
        )}
        
        <div className="register-container" style={{ 
          maxWidth: '750px', 
          margin: '0 auto',
          padding: '2.5rem',
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.08)'
        }}>
          <form onSubmit={handleSubmit}>
            {renderStepContent()}
          </form>
        </div>
        
        {step < 5 && (
          <div style={{ 
            textAlign: 'center', 
            marginTop: '2rem', 
            fontSize: '1rem',
            color: '#555'
          }}>
            Already a member? <Link to="/member-login" style={{ color: 'var(--primary-color)', fontWeight: '600' }}>Login here</Link>
          </div>
        )}
      </section>
      
      <Footer />
    </div>
  );
}

export default RegisterPage;
